import React, { HTMLAttributes, ThHTMLAttributes, TdHTMLAttributes } from 'react';

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  isSelected?: boolean;
  children: React.ReactNode;
}

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  sortable?: boolean;
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
}

/**
 * Table Component System - Notion-inspired design
 *
 * Specifications:
 * - Header: cream-50 background, 12px uppercase text
 * - Rows: 48px height, 14px text
 * - Hover: cream-100 background
 * - Border: border-light
 * - Border radius: 12px on container
 */

export const TableContainer: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`w-full overflow-x-auto rounded-lg border border-border-light scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 ${className}`}>
      {children}
    </div>
  );
};

export const Table: React.FC<TableProps> = ({ children, className = '', ...props }) => {
  return (
    <table
      className={`w-full min-w-160 border-collapse text-base text-black ${className}`}
      {...props}
    >
      {children}
    </table>
  );
};

export const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <thead
      className={`bg-cream-50 border-b-2 border-border-light ${className}`}
      {...props}
    >
      {children}
    </thead>
  );
};

export const TableBody: React.FC<TableBodyProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
};

export const TableRow: React.FC<TableRowProps> = ({
  isSelected = false,
  children,
  className = '',
  ...props
}) => {
  const baseClasses = 'border-b border-border-light transition-colors duration-200';
  const stateClasses = isSelected
    ? 'bg-orange-50 border-l-3 border-l-orange-400'
    : 'hover:bg-cream-100';

  return (
    <tr
      className={`${baseClasses} ${stateClasses} ${className}`}
      {...props}
    >
      {children}
    </tr>
  );
};

export const TableHead: React.FC<TableHeadProps> = ({
  sortable = false,
  children,
  className = '',
  ...props
}) => {
  const baseClasses =
    'px-4 py-3 text-left text-xs font-bold text-black-lighter uppercase tracking-wide';
  const sortableClasses = sortable
    ? 'cursor-pointer select-none hover:bg-cream-100'
    : '';

  return (
    <th
      className={`${baseClasses} ${sortableClasses} ${className}`}
      {...props}
    >
      {children}
    </th>
  );
};

export const TableCell: React.FC<TableCellProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <td
      className={`px-4 py-3 align-middle ${className}`}
      {...props}
    >
      {children}
    </td>
  );
};

/**
 * Table Pagination Component
 */
export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const startItem = totalItems && itemsPerPage ? (currentPage - 1) * itemsPerPage + 1 : null;
  const endItem =
    totalItems && itemsPerPage
      ? Math.min(currentPage * itemsPerPage, totalItems)
      : null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-white border-t border-border-light">
      <div className="text-sm text-black-lighter text-center sm:text-left">
        {startItem && endItem && totalItems && (
          <span>
            Showing {startItem} to {endItem} of {totalItems} results
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-md border border-border hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 touch-manipulation"
          aria-label="Previous page"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((page) => {
            // On mobile, show current page and adjacent pages only
            if (totalPages <= 5) return true;
            return Math.abs(page - currentPage) <= 1 || page === 1 || page === totalPages;
          })
          .map((page, index, array) => {
            // Add ellipsis where needed
            const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
            return (
              <React.Fragment key={page}>
                {showEllipsisBefore && (
                  <span className="hidden sm:inline px-2 text-black-lighter">...</span>
                )}
                <button
                  onClick={() => onPageChange(page)}
                  className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-200 touch-manipulation ${
                    page === currentPage
                      ? 'bg-orange-400 text-white'
                      : 'border border-border hover:bg-cream-100 text-black-light'
                  }`}
                  aria-label={`Page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              </React.Fragment>
            );
          })}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-md border border-border hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 touch-manipulation"
          aria-label="Next page"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
